import { Alert, Button, Card, Collapse, Descriptions, Form, Select, Space, Table, Tag, Typography } from 'antd'
import { useState } from 'react'
import { useSidebarTenantsQuery } from '../../MyTenant/tenantsQueries'
import { filterTenantOption, toTenantOptions } from '../../MyTenant/tenantSelectModel'
import { useRebuildableJobsQuery } from '../RebuildSnapshots/rebuildSnapshotsQueries'
import { filterJobOption, toJobOptions } from '../RebuildSnapshots/rebuildSnapshotsModel'
import { useInvoiceCheckMutation } from './invoiceCheckerQueries'
import { InvoiceCheckResult, WorkareaInvoiceCheck } from './invoiceCheckerApi'
import {
  FindingRow,
  expandedWorkareaKeys,
  findingCountLabel,
  getInvoiceCheckErrorMessage,
  getResultSummary,
  hasFindings,
  sortWorkareasByFindings,
  toFindingRows,
  toInvoiceCheckRequest,
  toWorkareaTotals,
  workareaKey,
} from './invoiceCheckerModel'
import { getJobNotFoundMessage, isJobNotFoundError, workareaPricingLabel, workareaTitle } from '../adminJobLookupModel'

const { Paragraph } = Typography

const findingColumns = [
  { title: 'Issue', dataIndex: 'issue', key: 'issue' },
  { title: 'Item', dataIndex: 'item', key: 'item' },
  { title: 'Kind', dataIndex: 'kind', key: 'kind', width: 70 },
  { title: 'Visit date', dataIndex: 'visitDate', key: 'visitDate', width: 110 },
  { title: 'Invoices', dataIndex: 'invoiceNumbers', key: 'invoiceNumbers', width: 110 },
  { title: 'Invoiced', dataIndex: 'invoicedAmount', key: 'invoicedAmount', width: 110, align: 'right' as const },
  { title: 'Current', dataIndex: 'currentAmount', key: 'currentAmount', width: 110, align: 'right' as const },
  { title: 'Difference', dataIndex: 'difference', key: 'difference', width: 110, align: 'right' as const },
]

const WorkareaHeader = ({ workarea }: { workarea: WorkareaInvoiceCheck }) => (
  <Space wrap>
    <span>{workareaTitle(workarea)}</span>
    <Tag>{workareaPricingLabel(workarea)}</Tag>
    <Tag color={hasFindings(workarea) ? 'red' : 'green'}>{findingCountLabel(workarea)}</Tag>
  </Space>
)

const WorkareaDetails = ({ workarea }: { workarea: WorkareaInvoiceCheck }) => (
  <Space direction="vertical" size={16} style={{ width: '100%' }}>
    <Descriptions bordered size="small" column={1}>
      {toWorkareaTotals(workarea).map(total => (
        <Descriptions.Item key={total.label} label={total.label}>{total.value}</Descriptions.Item>
      ))}
    </Descriptions>
    {hasFindings(workarea) && (
      <Table<FindingRow>
        size="small"
        columns={findingColumns}
        dataSource={toFindingRows(workarea.findings)}
        pagination={false}
        scroll={{ x: true }}
      />
    )}
  </Space>
)

const CheckResult = ({ result }: { result: InvoiceCheckResult }) => {
  const workareas = sortWorkareasByFindings(result.workareas)
  const anyFindings = workareas.some(hasFindings)
  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Alert type={anyFindings ? 'warning' : 'success'} showIcon message={getResultSummary(result)} />
      {workareas.length > 0 && (
        <Collapse
          defaultActiveKey={expandedWorkareaKeys(workareas)}
          items={workareas.map(workarea => ({
            key: workareaKey(workarea),
            label: <WorkareaHeader workarea={workarea} />,
            children: <WorkareaDetails workarea={workarea} />,
          }))}
        />
      )}
    </Space>
  )
}

const InvoiceCheckerSection = () => {
  const [tenantId, setTenantId] = useState<number | undefined>(undefined)
  const [jobId, setJobId] = useState<number | undefined>(undefined)
  const tenantsQuery = useSidebarTenantsQuery()
  const jobsQuery = useRebuildableJobsQuery(tenantId, true)
  const mutation = useInvoiceCheckMutation()

  const request = toInvoiceCheckRequest({ tenantId, jobId })

  const handleCheck = () => {
    if (request === null) {
      return
    }
    mutation.mutate(request)
  }

  return (
    <Card title="Invoice Checker" style={{ maxWidth: 1200 }}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          Finds the items and labor behind a job's time-and-materials or flat-rate invoicing
          discrepancies, such as unbilled money on the invoice screen that can't be selected.
          Read-only.
        </Paragraph>

        <Form layout="vertical" style={{ marginBottom: 0 }} onFinish={handleCheck}>
          <Form.Item label="Tenant">
            <Select
              showSearch
              placeholder="Search for a tenant"
              value={tenantId}
              onChange={(value: number) => {
                setTenantId(value)
                setJobId(undefined)
                mutation.reset()
              }}
              options={toTenantOptions(tenantsQuery.data ?? [])}
              filterOption={filterTenantOption}
              loading={tenantsQuery.isLoading}
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item label="Job">
            <Select
              showSearch
              placeholder={tenantId === undefined ? 'Select a tenant first' : 'Search for a job'}
              value={jobId}
              onChange={(value: number) => {
                setJobId(value)
                mutation.reset()
              }}
              options={toJobOptions(jobsQuery.data ?? [])}
              filterOption={filterJobOption}
              loading={jobsQuery.isLoading}
              disabled={tenantId === undefined}
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={mutation.isLoading}
            disabled={request === null}
          >
            Check job
          </Button>
        </Form>

        {jobsQuery.isError && (
          <Alert
            type="error"
            showIcon
            message="Failed to load the tenant's jobs"
            description="Change the tenant or re-select it to try again."
          />
        )}

        {mutation.isSuccess && <CheckResult result={mutation.data} />}

        {mutation.isError && isJobNotFoundError(mutation.error) && (
          <Alert
            type="warning"
            showIcon
            message="Job not found"
            description={getJobNotFoundMessage(mutation.error, mutation.variables ?? null)}
          />
        )}

        {mutation.isError && !isJobNotFoundError(mutation.error) && (
          <Alert
            type="error"
            showIcon
            message="Check failed"
            description={getInvoiceCheckErrorMessage(mutation.error)}
          />
        )}
      </Space>
    </Card>
  )
}

export default InvoiceCheckerSection
